import {Provider} from '@angular/core';
import {TEST_MODULE_REGISTRY, TestModuleDefinition} from 'features/core/shared/test/data/test-harness.types';
import {CpConfigPage} from '../page/cp-config-page/cp-config.page';
import {CpDashboardPage} from '../page/cp-dashboard-page/cp-dashboard.page';
import {
    CpProvisionDashboardStartComponent
} from '../component/protected-components/cp-provision-dashboard-start/cp-provision-dashboard-start.component';
import {
    CpProvisionRunsComponent
} from '../component/protected-components/cp-provision-runs/cp-provision-runs.component';
import {
    CpProvisionRunDetailComponent
} from '../component/protected-components/cp-provision-run-detail/cp-provision-run-detail.component';
import {
    CpProvisionAgentsComponent
} from '../component/protected-components/cp-provision-agents/cp-provision-agents.component';
import {
    CpProvisionRunStatusFlowComponent
} from '../component/protected-components/cp-provision-run-status-flow/cp-provision-run-status-flow.component';
import {
    CpProvisionAgentObjectComponent
} from '../component/object-components/cp-provision-agent-object/cp-provision-agent-object.component';
import {
    CpProvisionAgentFullComponent
} from '../component/object-components/cp-provision-agent-object/view/full/cp-provision-agent-full.component';
import {
    CpProvisionAgentSideComponent
} from '../component/object-components/cp-provision-agent-object/view/side/cp-provision-agent-side.component';
import {
    CpProvisionAgentBaseDataComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-base-data/cp-provision-agent-base-data.component';
import {
    CpProvisionAgentBalanceComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-balance/cp-provision-agent-balance.component';
import {
    CpProvisionAgentAccountComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-account/cp-provision-agent-account.component';
import {
    CpProvisionAgentManualBookingDialogComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-manual-booking-dialog/cp-provision-agent-manual-booking-dialog.component';
import {
    CpProvisionAgentTransferDialogComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-transfer-dialog/cp-provision-agent-transfer-dialog.component';
import {
    CpProvisionAgentPayoutsComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-payouts/cp-provision-agent-payouts.component';
import {
    CpProvisionAgentGroupsComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-groups/cp-provision-agent-groups.component';
import {
    CpProvisionAgentReserveCoverageComponent
} from '../component/object-components/cp-provision-agent-object/shared/cp-provision-agent-reserve-coverage/cp-provision-agent-reserve-coverage.component';
import {
    CpConfigCheckPanelComponent
} from '../component/shared/cp-config-check-panel/cp-config-check-panel.component';

/**
 * Test shell registration of the cp module (provision outgoing).
 *
 * Following the pattern of the neighbouring modules (`features/feature-bm/bm/test/bm-test-definition.ts`,
 * `features/feature-tech/jb/test/jb-test-definition.ts`): one TestModuleDefinition entry per
 * module that lists the components testable in isolation in the widget-test-shell.
 * Wired into cp.module.ts under `providers` as CP_TEST_PROVIDERS (04-frontend.md §1).
 *
 * The entry grows along with WP-16/WP-17: new cp components (step bar, KPI tiles,
 * OBC views) are added here; `testCases` with local mock data following the bm original
 * (TestCaseDataType.local) are possible but optional.
 */
const cpModuleEntry: TestModuleDefinition = {
    key: 'cp',
    label: 'Provisionsausgang (cp)',
    components: [
        {
            key: 'CpConfigPage',
            component: CpConfigPage,
            selector: 'cp-config-page',
            type: 'PageComponent',
            label: 'cp config page'
        },
        {
            key: 'CpDashboardPage',
            component: CpDashboardPage,
            selector: 'cp-dashboard-page',
            type: 'PageComponent',
            label: 'cp dashboard page'
        },
        {
            key: 'CpProvisionDashboardStartComponent',
            component: CpProvisionDashboardStartComponent,
            selector: 'cp-provision-dashboard-start',
            type: 'Component',
            label: 'cp provision dashboard start component'
        },
        {
            key: 'CpProvisionRunsComponent',
            component: CpProvisionRunsComponent,
            selector: 'cp-provision-runs',
            type: 'Component',
            label: 'cp provision runs component'
        },
        {
            key: 'CpProvisionRunDetailComponent',
            component: CpProvisionRunDetailComponent,
            selector: 'cp-provision-run-detail',
            type: 'Component',
            label: 'cp provision run detail component'
        },
        {
            key: 'CpProvisionRunStatusFlowComponent',
            component: CpProvisionRunStatusFlowComponent,
            selector: 'cp-provision-run-status-flow',
            type: 'Component',
            label: 'cp provision run status flow component'
        },
        {
            key: 'CpProvisionAgentsComponent',
            component: CpProvisionAgentsComponent,
            selector: 'cp-provision-agents',
            type: 'Component',
            label: 'cp provision agents component'
        },
        {
            key: 'CpProvisionAgentObjectComponent',
            component: CpProvisionAgentObjectComponent,
            selector: 'cp-provision-agent-object',
            type: 'ObjectBaseComponent',
            label: 'cp provision agent object component'
        },
        {
            key: 'CpProvisionAgentFullComponent',
            component: CpProvisionAgentFullComponent,
            selector: 'cp-provision-agent-full',
            type: 'Component',
            label: 'cp provision agent object - full view'
        },
        {
            key: 'CpProvisionAgentSideComponent',
            component: CpProvisionAgentSideComponent,
            selector: 'cp-provision-agent-side',
            type: 'Component',
            label: 'cp provision agent object - side view'
        },
        {
            key: 'CpProvisionAgentBaseDataComponent',
            component: CpProvisionAgentBaseDataComponent,
            selector: 'cp-provision-agent-base-data',
            type: 'Component',
            label: 'cp provision agent - Basisdaten'
        },
        {
            key: 'CpProvisionAgentBalanceComponent',
            component: CpProvisionAgentBalanceComponent,
            selector: 'cp-provision-agent-balance',
            type: 'Component',
            label: 'cp provision agent - Konten/Salden'
        },
        {
            key: 'CpProvisionAgentAccountComponent',
            component: CpProvisionAgentAccountComponent,
            selector: 'cp-provision-agent-account',
            type: 'Component',
            label: 'cp provision agent - Konten & Salden (Kontoauszug)'
        },
        {
            key: 'CpProvisionAgentManualBookingDialogComponent',
            component: CpProvisionAgentManualBookingDialogComponent,
            selector: 'cp-provision-agent-manual-booking-dialog',
            type: 'Component',
            label: 'cp provision agent - Dialog Manuelle Buchung'
        },
        {
            key: 'CpProvisionAgentTransferDialogComponent',
            component: CpProvisionAgentTransferDialogComponent,
            selector: 'cp-provision-agent-transfer-dialog',
            type: 'Component',
            label: 'cp provision agent - Dialog Umbuchung'
        },
        {
            key: 'CpProvisionAgentPayoutsComponent',
            component: CpProvisionAgentPayoutsComponent,
            selector: 'cp-provision-agent-payouts',
            type: 'Component',
            label: 'cp provision agent - Auszahlungen pro Monat'
        },
        {
            key: 'CpProvisionAgentGroupsComponent',
            component: CpProvisionAgentGroupsComponent,
            selector: 'cp-provision-agent-groups',
            type: 'Component',
            label: 'cp provision agent - Gruppen'
        },
        {
            key: 'CpProvisionAgentReserveCoverageComponent',
            component: CpProvisionAgentReserveCoverageComponent,
            selector: 'cp-provision-agent-reserve-coverage',
            type: 'Component',
            label: 'cp provision agent - Runs/Deckung'
        },
        {
            key: 'CpConfigCheckPanelComponent',
            component: CpConfigCheckPanelComponent,
            selector: 'cp-config-check-panel',
            type: 'Component',
            label: 'cp config check panel component'
        }
    ]
};

export const CP_TEST_PROVIDERS: Provider[] = [
    {provide: TEST_MODULE_REGISTRY, useValue: cpModuleEntry, multi: true}
];
